import { useEffect, useRef } from 'react';

import { useLocation } from '@remix-run/react';

declare global {
  interface Window {
    twq: any;
  }
}

interface PixelProps {
  pixelId: string;
  marketingCookies: boolean;
}

const TwitterPixel = ({ pixelId, marketingCookies }: PixelProps) => {
  const location = useLocation();
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const idleCallbackIdRef = useRef<number>();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  // Fire page view on each route change
  useEffect(() => {
    try {
      if (window.twq) {
        window.twq('track', 'PageView');
      }
    } catch {
      /*swallow exception*/
    }
  }, [location]);

  useEffect(() => {
    let isMounted = true;

    const loadPixel = () => {
      if (!isMounted) return;

      try {
        const trimmedId = pixelId?.trim();
        // match valid pixel id: 5+ lowercase alphanumeric chars
        if (!trimmedId?.match(/^[a-z0-9]{5,}$/) || !marketingCookies) {
          return;
        }

        // Prevent duplicate script injection
        if (document.getElementById('twitter-pixel')) {
          return;
        }

        const script = document.createElement('script');
        script.id = 'twitter-pixel';
        script.async = true;
        script.innerHTML = `
          (function(){try{
            !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
            },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='//static.ads-twitter.com/uwt.js',
            a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
            // Insert Twitter Pixel ID and Standard Event data below
            twq('init','${trimmedId}');
          }catch{/*swallow exception*/}})();
        `;
        document.head.appendChild(script);
        scriptRef.current = script;
      } catch {
        /*swallow exception*/
      }
    };

    // Defer pixel loading with timeout to ensure it fires
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackIdRef.current = window.requestIdleCallback(loadPixel, { timeout: 2000 });
    } else {
      timeoutIdRef.current = setTimeout(loadPixel, 100);
    }

    // Return cleanup function
    return () => {
      isMounted = false;

      if (idleCallbackIdRef.current !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackIdRef.current);
      }
      if (timeoutIdRef.current !== undefined) {
        clearTimeout(timeoutIdRef.current);
      }

      try {
        if (scriptRef.current?.parentNode) {
          scriptRef.current.parentNode.removeChild(scriptRef.current);
        }
      } catch {
        // Script already removed, safe to ignore
      }
    };
  }, [pixelId, marketingCookies]);

  return null;
};

export default TwitterPixel;
